import { AppContext } from 'next/app'
import { ThemeProvider } from 'styled-components'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useRouter } from 'next/router'
import { NotifiableError } from '@bugsnag/core'

import AppSagas from '../redux/app/sagas'
import { GlobalStyles, themes } from '../theme'
import { ExtendedStore, wrapper } from '../redux/store'
import bugsnagClient from '../helpers/BugsnagHelpers'
import { actions, selectors } from '../redux'
import I18nProvider from '../i18n/Provider'
import { app } from '../configuration'
import FeedbackWrapper from '../components/Feedback/wrapper'
import { getAuthCookie } from '../helpers/CookieHelpers'

import NotFound from './404'
import ErrorPage from './_error'
import OfflinePage from './_offline'

const Application = (props: any) => {
  const { Component, store, ...pageProps } = props
  const nextRouter = useRouter()
  const dispatch = useDispatch()
  const isOffline = useSelector(selectors.app.isOffline)

  const handleRouteChange = useCallback(() => {
    // TODO: Refactor to properly mutate isGenerating after HYDRATE
    setTimeout(() => {
      dispatch(actions.user.setIsGeneratingPlanning(false))
    }, 300)
  }, [dispatch])

  useEffect(() => {
    nextRouter.events.on('routeChangeComplete', handleRouteChange)

    return () => {
      nextRouter.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [nextRouter, handleRouteChange])

  useEffect(() => {
    if (navigator.onLine) {
      dispatch(actions.app.mount())
    } else if (!navigator.onLine && getAuthCookie()) {
      dispatch(actions.app.mountOffline())
    }
  }, [dispatch])

  useEffect(() => {
    const onlineHandler = () =>
      dispatch(actions.app.setIsOffline({ isOffline: false }))
    const offlineHandler = () =>
      dispatch(actions.app.setIsOffline({ isOffline: true }))
    window.addEventListener('online', onlineHandler)
    window.addEventListener('offline', offlineHandler)
    if (navigator.onLine) {
      onlineHandler()
    } else {
      offlineHandler()
    }
    return () => {
      window.removeEventListener('online', onlineHandler)
      window.removeEventListener('offline', offlineHandler)
    }
  }, [dispatch])

  useEffect(() => {
    if (!app.USE_SERVICE_WORKER) {
      try {
        navigator?.serviceWorker?.getRegistrations().then((registrations) => {
          // eslint-disable-next-line no-restricted-syntax
          for (const registration of registrations) {
            registration.unregister()
          }
        })
      } catch (e) {
        console.log('service worker unregister error', e)
      }
    }
  }, [])

  return (
    <>
      <GlobalStyles />
      <ThemeProvider theme={themes['theme']}>
        <I18nProvider store={store?.i18n}>
          <>
            {props?.error ? (
              props?.error === 404 && !isOffline ? (
                <NotFound />
              ) : isOffline ? (
                <OfflinePage />
              ) : (
                <ErrorPage {...pageProps} />
              )
            ) : (
              <Component {...pageProps} />
            )}
            <FeedbackWrapper />
          </>
        </I18nProvider>
      </ThemeProvider>
    </>
  )
}

Application.getInitialProps = async (appContext: AppContext) => {
  try {
    const props = await (appContext.ctx?.store as ExtendedStore)?.saga
      .run(AppSagas.init, appContext.ctx, appContext.Component?.getInitialProps)
      .toPromise()

    return {
      ...props,
      store: appContext.ctx?.store?.getState() ?? {},
    }
  } catch (e) {
    if (bugsnagClient) {
      bugsnagClient.notify(e as NotifiableError)
    }
    console.error(e)
  }
}

export default wrapper.withRedux(Application)
